import Navbar from '../components/Navbar';
import { Helmet } from 'react-helmet-async';
import { Mail, ArrowRight } from 'lucide-react';

export default function ContactPage() {
  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-950">
      <Helmet>
        <title>Contact | CalcByEA™</title>
        <meta name="description" content="Get in touch with CalcByEA. For support, refunds, or complaints about paying for a calculator." />
        <link rel="canonical" href="https://calculatorbyea.com/contact" />
        <meta property="og:url" content="https://calculatorbyea.com/contact" />
        <meta property="og:title" content="Contact | CalcByEA™" />
        <meta property="og:description" content="Get in touch with CalcByEA. For support, refunds, or complaints about paying for a calculator." />
        <meta name="twitter:title" content="Contact | CalcByEA™" />
        <meta name="twitter:description" content="For support, refunds, or complaints about paying for a calculator." />
      </Helmet>
      <Navbar />

      <div className="pt-14 max-w-3xl mx-auto px-6 pb-20">

        {/* Header */}
        <div className="border-b-2 border-zinc-950 pt-16 pb-10 mb-12">
          <div className="text-[9px] font-bold uppercase tracking-[0.35em] text-zinc-400 mb-3">Premium Support™</div>
          <h1 className="font-bebas text-6xl sm:text-7xl text-zinc-950 leading-none mb-4">Contact</h1>
          <p className="text-zinc-500 text-base max-w-md">
            We respond to all emails. Unlike most game publishers.
          </p>
        </div>

        {/* Main contact card */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 border-2 border-zinc-950 shadow-[6px_6px_0px_0px_rgba(9,9,11,1)] mb-10">
          <div className="bg-zinc-950 text-white p-8 flex flex-col justify-between">
            <div>
              <div className="text-[9px] font-bold uppercase tracking-[0.3em] text-zinc-500 mb-4">General Enquiries</div>
              <div className="font-bebas text-3xl text-white leading-tight mb-3">
                Got a question?<br />Drop us a line.
              </div>
              <p className="text-zinc-500 text-xs leading-relaxed">
                For support, refunds, bugs, partnerships, or complaints about the price of the equals sign.
              </p>
            </div>
            <div className="mt-8 flex items-center gap-2 text-zinc-500 text-xs">
              <Mail size={12} strokeWidth={2} />
              <span className="uppercase tracking-widest font-bold text-[10px]">Response within 48h</span>
            </div>
          </div>

          <div className="bg-white p-8 flex flex-col justify-center">
            <div className="text-[9px] font-bold uppercase tracking-[0.3em] text-zinc-400 mb-2">Email Us</div>
            <a
              href="mailto:contact@itorzo.com"
              className="font-bebas text-2xl text-zinc-950 hover:text-zinc-600 transition-colors duration-100 flex items-center gap-3 group"
              data-testid="contact-email-link"
            >
              contact@itorzo.com
              <ArrowRight size={16} strokeWidth={2.5} className="group-hover:translate-x-1 transition-transform duration-100" />
            </a>
            <p className="text-xs text-zinc-400 mt-4 leading-relaxed">
              Clicking this opens your email client. No DLC required.
            </p>
          </div>
        </div>

        {/* FAQ rows */}
        <div className="border-t-2 border-zinc-950 mb-2">
          <div className="text-[9px] font-bold uppercase tracking-[0.3em] text-zinc-400 mt-6 mb-5">Common Questions</div>
        </div>

        {[
          {
            q: "I paid but my buttons are still locked.",
            a: "Log out and log back in — your unlocks sync on login. If it persists after 5 minutes, email us with your payment email and we'll fix it manually.",
          },
          {
            q: "Can I get a refund?",
            a: "Yes. Email us within 14 days of purchase with your payment email. We'll process it, no questions asked. (We're not actually EA.)",
          },
          {
            q: "I want to partner, write about this, or do something funny with it.",
            a: "Please do. Email us. This thing exists to be written about.",
          },
          {
            q: "Is this a parody?",
            a: "Yes. EA is a trademark of Electronic Arts. We are not affiliated with EA. But the checkout is real.",
          },
        ].map(({ q, a }) => (
          <div key={q} className="border-b border-zinc-100 py-5">
            <div className="text-sm font-bold text-zinc-950 mb-1.5">{q}</div>
            <div className="text-sm text-zinc-500 leading-relaxed">{a}</div>
          </div>
        ))}

        {/* Footer nudge */}
        <div className="mt-12 border-2 border-zinc-200 bg-white px-6 py-5 flex items-center justify-between">
          <div>
            <div className="text-[9px] font-bold uppercase tracking-[0.3em] text-zinc-400 mb-1">Still here?</div>
            <p className="text-sm text-zinc-950 font-semibold">Go calculate something.</p>
            <p className="text-xs text-zinc-400 mt-0.5">The zero button is free. That's one more than EA would give you.</p>
          </div>
          <a
            href="/calculator"
            className="flex items-center gap-2 bg-zinc-950 text-white font-bold uppercase tracking-widest text-xs px-5 py-3 border-2 border-zinc-950 shadow-[3px_3px_0px_0px_rgba(9,9,11,1)] hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[5px_5px_0px_0px_rgba(9,9,11,1)] transition-all duration-100 shrink-0"
          >
            Try It <ArrowRight size={11} strokeWidth={3} />
          </a>
        </div>

      </div>

      {/* Footer */}
      <footer className="py-10 px-6 bg-zinc-50 border-t border-zinc-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-center sm:text-left">
              <p className="text-[10px] font-bold text-zinc-300 uppercase tracking-widest leading-loose">
                EA is a trademark of Electronic Arts. This site is a parody. Not affiliated with EA. But we did take notes.
              </p>
              <p className="text-[10px] font-medium text-zinc-300 mt-1">
                <a href="https://itorzo.com" target="_blank" rel="noopener noreferrer" className="hover:text-zinc-500 transition-colors">An Itorzo product</a>
              </p>
            </div>
            <div className="flex items-center gap-5 shrink-0">
              <a href="/privacy" className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest hover:text-zinc-950 transition-colors">Privacy Policy</a>
              <a href="/terms" className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest hover:text-zinc-950 transition-colors">Terms</a>
              <a href="/about" className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest hover:text-zinc-950 transition-colors">About</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
